import React from 'react';
import PropTypes from 'prop-types';
import {Link, useNavigate} from 'react-router-dom';
import classNames from 'classnames';
import {Avatar, Divider, Dropdown, Modal, Tooltip} from 'antd';
import _ from 'lodash';

import {APP_TITLE, showAtDev} from '@/env';
import {
    AppstoreAddOutlined,
    ControlTwoTone,
    LockOutlined,
    LogoutOutlined,
    MessageOutlined,
    TeamOutlined,
    UserOutlined,
} from '@/icons';
import {AvatarLabel} from '@/component';
import {useAuthStore} from '@/context/auth';
import {useTopNavAuthService} from '@/page/admin/adminService';

import styles from './AppLayoutHeader.module.less';

const AppLayoutHeader = ({enabledHomeLink = true, subTitle, className, style}) => {
    const [authStore] = useAuthStore();
    const navAuth = useTopNavAuthService();
    const navigate = useNavigate();

    function onMenuClick({key}) {
        if (key.startsWith('/')) {
            navigate(key);
        } else if (key === 'profile') {
            navigate('/profile');
        } else if (key === 'logout') {
            Modal.confirm({
                title: '退出系统',
                content: '确定退出当前登录？',
                okButtonProps: {
                    danger: true,
                },
                onOk: () => {
                    navigate('/login');
                },
            });
        }
    }

    // antd 5: Dropdown 使用 menu={{items}}，不再支持 overlay={<Menu>}
    const items = [
        {key: 'profile', icon: <UserOutlined />, label: '个人信息'},
        {type: 'divider', key: 'divider-1'},
        {key: 'logout', icon: <LogoutOutlined />, label: '退出', danger: true},
    ];

    // 客户管理下拉子菜单
    const customerItems = [
        {key: '/customer/manage', label: '客户列表'},
        {key: '/customer/tag', label: '客户标签'},
        {key: '/customer/room', label: '客户群管理'},
    ];

    // 会话管理下拉子菜单
    const conversationItems = [
        {key: '/conversation/external', label: '外部会话'},
        {key: '/conversation/internal', label: '内部会话'},
        {key: '/conversation/customer', label: '客户会话'},
        {key: '/conversation/advance', label: '高级会话'},
    ];

    function renderHomeTitle() {
        return (
            <div className={styles.title}>
                <img
                    src={require('@/asset/bi-logo_blue.svg').default}
                    alt="logo"
                    style={{height: 30}}
                />
                <h1>{APP_TITLE}</h1>
            </div>
        );
    }

    return (
        <div className={classNames(styles.container, className)} style={style}>
            <div className={styles.titleWrap}>
                {enabledHomeLink ? (
                    <Tooltip title="返回首页" placement="right">
                        <Link to="/">{renderHomeTitle()}</Link>{' '}
                    </Tooltip>
                ) : (
                    renderHomeTitle()
                )}

                <h2 className={styles.subTitle}>{subTitle}</h2>
            </div>
            <div className={styles.actions}>
                {showAtDev && (
                    <>
                        <div className={styles.action}>
                            <Link to="/dev/icons">
                                <Avatar icon={<AppstoreAddOutlined />} style={{marginRight: 10}} />
                                Icons
                            </Link>
                        </div>
                        <Divider type="vertical" className={styles.divider} />
                    </>
                )}

                {navAuth.customerEntry && (
                    <>
                        <div className={styles.action}>
                            <Dropdown
                                arrow
                                menu={{items: customerItems, onClick: onMenuClick}}
                                placement="bottom"
                            >
                                <div>
                                    <Avatar icon={<TeamOutlined />} style={{marginRight: 10}} />
                                    客户管理
                                </div>
                            </Dropdown>
                        </div>
                        <Divider type="vertical" className={styles.divider} />
                    </>
                )}
                {navAuth.conversationEntry && (
                    <>
                        <div className={styles.action}>
                            <Dropdown
                                arrow
                                menu={{items: conversationItems, onClick: onMenuClick}}
                                placement="bottom"
                            >
                                <div>
                                    <Avatar icon={<MessageOutlined />} style={{marginRight: 10}} />
                                    会话管理
                                </div>
                            </Dropdown>
                        </div>
                        <Divider type="vertical" className={styles.divider} />
                    </>
                )}
                {navAuth.adminEntry && (
                    <>
                        <div className={styles.action}>
                            <Link to="/admin">
                                <Avatar icon={<ControlTwoTone />} style={{marginRight: 10}} />
                                系统管理
                            </Link>
                        </div>
                        <Divider type="vertical" className={styles.divider} />
                    </>
                )}
                <div className={styles.action}>
                    <Dropdown
                        arrow
                        menu={{items, onClick: onMenuClick, className: styles.menu}}
                        placement="bottom"
                    >
                        <div>
                            <AvatarLabel text={_.get(authStore, 'user.name')} />
                        </div>
                    </Dropdown>
                </div>
            </div>
        </div>
    );
};

AppLayoutHeader.propTypes = {
    enabledHomeLink: PropTypes.bool,
    subTitle: PropTypes.node,
    className: PropTypes.string,
    style: PropTypes.object,
    children: PropTypes.node,
};
export default AppLayoutHeader;
